import { useState, useEffect, useRef, useCallback } from 'react';
import { formatPhone } from '../components/formatPhone';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import ConfirmModal from '../components/ConfirmModal';
import Sidebar from './Sidebar';
import Navbar from './Navbar';
import { FaPlus, FaHome, FaBuilding, FaCity, FaChevronRight, FaChevronDown, FaChevronUp, FaMapMarkerAlt, FaChartBar, FaDollarSign, FaSlidersH, FaTimes, FaSearch, FaCheck, FaMagic, FaUpload, FaFilePdf, FaSpinner } from 'react-icons/fa';
import './Sidebar.css';
import './Navbar.css';
import './Properties.css';
import Tutorial from './Tutorial';

function Properties() {
  const navigate = useNavigate();
  const [initials, setInitials] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [confirmDialog, setConfirmDialog] = useState(null);
  const askConfirm = (opts, onConfirm) => setConfirmDialog({ ...opts, onConfirm });
  const [pmId, setPmId] = useState(null);
  const [properties, setProperties] = useState([]);
  const [demoMode, setDemoMode] = useState(false);
  const [expandedPropertyId, setExpandedPropertyId] = useState(null);
  const [showFilter, setShowFilter] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterAddress, setFilterAddress] = useState('');
  const [filterType, setFilterType] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');

  const BASE_URL = 'https://api.rside.app';
  const MAX_LEASE_SIZE = 10 * 1024 * 1024; // 10 MB
  const todayISO = new Date().toISOString().slice(0, 10);
  const nextYearISO = (() => { const d = new Date(); d.setFullYear(d.getFullYear() + 1); return d.toISOString().slice(0, 10); })();

  const toISO = (d) => {
    if (!d) return '';
    const raw = String(d).trim();
    // Already YYYY-MM-DD
    const isoM = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoM) return `${isoM[1]}-${isoM[2]}-${isoM[3]}`;
    // MM-DD-YYYY
    const mdyD = raw.match(/^(\d{1,2})-(\d{1,2})-(\d{4})/);
    if (mdyD) return `${mdyD[3]}-${mdyD[1].padStart(2,'0')}-${mdyD[2].padStart(2,'0')}`;
    // MM/DD/YYYY
    const mdyS = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (mdyS) return `${mdyS[3]}-${mdyS[1].padStart(2,'0')}-${mdyS[2].padStart(2,'0')}`;
    // "Month D, YYYY" or "Month D YYYY" — native parse (local time)
    const parsed = new Date(raw);
    if (!isNaN(parsed))
      return `${parsed.getFullYear()}-${String(parsed.getMonth()+1).padStart(2,'0')}-${String(parsed.getDate()).padStart(2,'0')}`;
    return '';
  };

  // ---- Leasing Center state ----
  const [showLC, setShowLC] = useState(false);
  const [lcStep, setLcStep] = useState('center'); // 'center' | 'form'
  const [lcPropertyId, setLcPropertyId] = useState('');
  const [lcDoorId, setLcDoorId] = useState('');
  const [lcUnitStatus, setLcUnitStatus] = useState('Vacant');
  const [lcVacantSaving, setLcVacantSaving] = useState(false);
  const [lcFees, setLcFees] = useState([{ name: '', amount: '' }]);
  const [lcAiFees, setLcAiFees] = useState([{ name: '', amount: '' }]);
  const [lcSubmitting, setLcSubmitting] = useState(false);
  const [lcError, setLcError] = useState('');
  const [lcForm, setLcForm] = useState({
    tenantName: '', phoneNumber: '', deposit: '', rent: '',
    leaseStartDate: todayISO, leaseEndDate: nextYearISO,
    rentDueDay: '1', leaseType: 'Fixed Term'
  });

  // AI Lease Import
  const [lcAiFiles, setLcAiFiles] = useState([]);
  const [lcAiForm, setLcAiForm] = useState({
    tenantName: '', phone: '', email: '', rent: '', deposit: '',
    leaseStart: todayISO, leaseEnd: nextYearISO, rentDue: '1', leaseType: 'Fixed Term',
    insuranceStart: '', insuranceEnd: ''
  });
  const [lcAiSaveDoc, setLcAiSaveDoc] = useState(false);
  const [lcAiError, setLcAiError] = useState('');
  const [lcAiConverting, setLcAiConverting] = useState(false);
  const lcAiFileRef = useRef(null);

  // Tutorial interaction refs — track which UI states the tutorial opened
  const tutorialCardExpanded = useRef(false);
  const tutorialFilterOpened = useRef(false);
  const firstPropertyIdRef   = useRef(null); // kept live so callback doesn't need properties in deps

  const handleTutorialStep = useCallback((stepIndex, stepConfig) => {
    const anchorId = stepConfig?.anchorId;

    // Tutorial closed / skipped — reset any states it opened
    if (stepIndex === -1) {
      if (tutorialCardExpanded.current) { setExpandedPropertyId(null); tutorialCardExpanded.current = false; }
      if (tutorialFilterOpened.current) { setShowFilter(false); tutorialFilterOpened.current = false; }
      if (demoMode) {
        localStorage.removeItem(`isDemoProps_${userEmail}`);
        setDemoMode(false);
        setProperties(prev => prev.filter(p => p.id !== 'demo-prop-1'));
      }
      return;
    }

    // Auto-expand the first property card
    if (anchorId === 'props-first-card') {
      const firstId = firstPropertyIdRef.current;
      if (firstId) { setExpandedPropertyId(firstId); tutorialCardExpanded.current = true; }
    } else if (tutorialCardExpanded.current) {
      setExpandedPropertyId(null);
      tutorialCardExpanded.current = false;
    }

    // Auto-open filter drawer
    if (anchorId === 'props-filter-drawer') {
      setShowFilter(true);
      tutorialFilterOpened.current = true;
    } else if (tutorialFilterOpened.current) {
      setShowFilter(false);
      tutorialFilterOpened.current = false;
    }
  }, [demoMode, userEmail]);

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (!user) return navigate('/login');
      if (localStorage.getItem(`isDemoProps_${user.email}`) === 'true') {
        localStorage.removeItem(`hasSeenPropertiesTutorial_${user.email}`);
      }
      setUserEmail(user.email);
      try {
        const accountRes = await fetch(`https://api.rside.app/MyAccount?Email=${encodeURIComponent(user.email)}`);
        if (accountRes.ok) {
          const accountData = await accountRes.json();
          setPmId(accountData.id || accountData.PM_ID || accountData.pm_id || null);
        }
        const res = await fetch(`https://api.rside.app/PropertyManagerName?email=${user.email}`);
        const data = await res.json();
        const parts = data.Full_Name?.split(' ') || [];
        setInitials((parts[0]?.[0] || '') + (parts.at(-1)?.[0] || ''));

        const propRes = await fetch(
          `https://api.rside.app/GetAllProperties?full_name=${encodeURIComponent(data.Full_Name)}&Email=${encodeURIComponent(user.email)}`
        );
        const propData = propRes.ok ? await propRes.json() : { Properties: [] };

        const propertiesMap = new Map();
        for (const p of propData.Properties || []) {
          const propId = p.Property_ID;
          if (!propertiesMap.has(propId)) {
            propertiesMap.set(propId, {
              id: propId,
              name: p.Property_Name,
              propertyType: parseInt(p.Num_Of_Units) > 1 ? 'Multi Family Home' : 'Single Family Home',
              numberOfUnits: p.Num_Of_Units,
              status: p.Status || 'Vacant',
              address: p.Street_Address,
              units: []
            });
          }
          propertiesMap.get(propId).units.push({
            unitNumber: p.Unit_Number,
            status: p.Status || 'Vacant',
            doorId: p.Door_id
          });
        }

        const mapped = Array.from(propertiesMap.values()).map((prop) => {
          if (parseInt(prop.numberOfUnits) > 1 && prop.units.length > 1) {
            const occ = prop.units.filter(u => u.status === 'Occupied').length;
            const vac = prop.units.filter(u => u.status === 'Vacant').length;
            prop.status = occ === prop.units.length ? 'Occupied' : vac === prop.units.length ? 'Vacant' : 'Partial';
          }
          return prop;
        });

        setProperties(mapped.sort((a, b) => (a.name || '').localeCompare(b.name || '')));
        if (localStorage.getItem(`isDemoProps_${user.email}`) === 'true') {
          setProperties(prev => prev.length === 0 ? [{
            id: 'demo-prop-1',
            name: 'Sample Property ✦',
            propertyType: 'Single Family Home',
            numberOfUnits: 1,
            status: 'Occupied',
            address: '123 Maple Street',
            units: [{ unitNumber: '', status: 'Occupied', doorId: 'demo-door-1' }]
          }] : prev);
          setDemoMode(true);
          localStorage.removeItem(`hasSeenPropertiesTutorial_${user.email}`);
        }
      } catch (err) {
        console.error('Error loading properties:', err);
      }
    });
    return unsub;
  }, [navigate]);

  // Keep tutorial ref in sync
  useEffect(() => { firstPropertyIdRef.current = properties[0]?.id ?? null; }, [properties]);

  const totalProperties = properties.length;
  const allUnits = properties.flatMap(p => p.units || []);
  const totalUnits = allUnits.length;
  const occupiedCount = allUnits.filter(u => u.status === 'Occupied').length;
  const vacantCount = allUnits.filter(u => u.status !== 'Occupied').length;

  const activeFilterCount = [
    searchQuery !== '',
    filterAddress !== '',
    filterType !== 'All',
    filterStatus !== 'All',
  ].filter(Boolean).length;

  const filteredProperties = properties.filter((p) => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (!p.name?.toLowerCase().includes(q) && !p.address?.toLowerCase().includes(q)) return false;
    }
    if (filterAddress) {
      if (!p.address?.toLowerCase().includes(filterAddress.toLowerCase())) return false;
    }
    if (filterType === 'Single Family' && p.propertyType !== 'Single Family Home') return false;
    if (filterType === 'Multi Family' && p.propertyType !== 'Multi Family Home') return false;
    if (filterStatus === 'Occupied' && p.status?.toLowerCase() !== 'occupied') return false;
    if (filterStatus === 'Vacant' && p.status?.toLowerCase() !== 'vacant') return false;
    if (filterStatus === 'Partial' && p.status?.toLowerCase() !== 'partial') return false;
    return true;
  });

  const resetFilters = () => {
    setSearchQuery('');
    setFilterAddress('');
    setFilterType('All');
    setFilterStatus('All');
  };

  // ---- Leasing Center helpers ----
  const lcProperty = properties.find(p => String(p.id) === String(lcPropertyId));
  const lcIsMulti = lcProperty ? parseInt(lcProperty.numberOfUnits) > 1 : false;

  const openLC = () => {
    setShowLC(true);
    setLcStep('center');
    setLcPropertyId('');
    setLcDoorId('');
    setLcUnitStatus('Vacant');
    setLcFees([{ name: '', amount: '' }]);
    setLcAiFees([{ name: '', amount: '' }]);
    setLcError('');
    setLcForm({ tenantName: '', phoneNumber: '', deposit: '', rent: '',
      leaseStartDate: todayISO, leaseEndDate: nextYearISO, rentDueDay: '1', leaseType: 'Fixed Term' });
  };

  const closeLC = () => { setShowLC(false); };

  const handleLcPropertyChange = (propId) => {
    setLcPropertyId(propId);
    const prop = properties.find(p => String(p.id) === String(propId));
    if (!prop) { setLcDoorId(''); setLcUnitStatus('Vacant'); return; }
    const isMulti = parseInt(prop.numberOfUnits) > 1;
    const firstUnit = prop.units[0];
    setLcDoorId(isMulti ? '' : (firstUnit?.doorId || ''));
    setLcUnitStatus(isMulti ? 'Vacant' : (firstUnit?.status === 'Occupied' ? 'Occupied' : 'Vacant'));
  };

  const handleLcUnitChange = (doorId) => {
    setLcDoorId(doorId);
    const unit = lcProperty?.units.find(u => u.doorId === doorId);
    setLcUnitStatus(unit?.status === 'Occupied' ? 'Occupied' : 'Vacant');
  };

  const handleMarkVacant = () => {
    askConfirm(
      {
        title: 'Mark Unit Vacant',
        message: 'This will delete all tenants and lease information for this unit. This action cannot be undone.',
        confirmLabel: 'Mark Vacant',
        variant: 'danger',
      },
      async () => {
    const doorId = lcIsMulti ? lcDoorId : lcProperty?.units[0]?.doorId;
    if (!doorId) return;
    setLcVacantSaving(true);
    setLcError('');
    try {
      await fetch(`${BASE_URL}/MoveTenantsToPast?Door_id=${doorId}`, { method: 'POST' });
      await fetch(`${BASE_URL}/DeleteLeaseByDoor?door_id=${doorId}`, { method: 'DELETE' });
      await fetch(`${BASE_URL}/UpdateDoorStatus?door_id=${doorId}&status=Vacant`, { method: 'PUT' });
      setProperties(prev => prev.map(p => {
        if (String(p.id) !== String(lcPropertyId)) return p;
        const updatedUnits = p.units.map(u => u.doorId === doorId
          ? { ...u, status: 'Vacant', tenantName: '', phoneNumber: '', deposit: '', rent: '', leaseStartDate: '', leaseEndDate: '', rentDueDate: '' }
          : u);
        const allOcc = updatedUnits.every(u => u.status === 'Occupied');
        const allVac = updatedUnits.every(u => u.status !== 'Occupied');
        return { ...p, units: updatedUnits, status: allOcc ? 'Occupied' : allVac ? 'Vacant' : 'Partial' };
      }));
      closeLC();
    } catch {
      setLcError('Failed to vacate unit. Please try again.');
    } finally {
      setLcVacantSaving(false);
    }
      }
    );
  };

  const submitLease = async () => {
    if (!lcDoorId || !lcForm.tenantName.trim() || !lcForm.rent) return;
    setLcSubmitting(true);
    setLcError('');
    try {
      await fetch(`${BASE_URL}/UpdateDoorStatus?door_id=${lcDoorId}&status=Occupied`, { method: 'PUT' });

      const leaseParams = new URLSearchParams({
        Rent: parseFloat(lcForm.rent) || 0,
        Deposit: parseFloat(lcForm.deposit) || 0,
        Start_Date: lcForm.leaseStartDate || '',
        End_Date: lcForm.leaseEndDate || '',
        Rent_Due: lcForm.rentDueDay || '1',
        MonthToMonth: lcForm.leaseType === 'Month-to-Month',
        Door_id: lcDoorId
      });
      const leaseRes = await fetch(`${BASE_URL}/AddLease?${leaseParams}`, { method: 'POST' });
      if (!leaseRes.ok) throw new Error('Failed to add lease');

      const tenantParams = new URLSearchParams({ Full_Name: lcForm.tenantName, Phone_Number: lcForm.phoneNumber, Door_id: lcDoorId });
      const tenantRes = await fetch(`${BASE_URL}/AddTenant?${tenantParams}`, { method: 'POST' });
      if (!tenantRes.ok) throw new Error('Failed to add tenant');

      const validFees = lcFees.filter(f => f.name.trim() && parseFloat(f.amount) > 0);
      await Promise.all(validFees.map(f =>
        fetch(`${BASE_URL}/AddFee?fee_type=${encodeURIComponent(f.name.trim())}&amount=${parseFloat(f.amount)}&door_id=${lcDoorId}`, { method: 'POST', headers: { accept: 'application/json' } })
      ));

      setProperties(prev => prev.map(p => {
        if (String(p.id) !== String(lcPropertyId)) return p;
        const updatedUnits = p.units.map(u => u.doorId === lcDoorId ? { ...u, status: 'Occupied' } : u);
        const allOcc = updatedUnits.every(u => u.status === 'Occupied');
        const allVac = updatedUnits.every(u => u.status !== 'Occupied');
        return { ...p, units: updatedUnits, status: allOcc ? 'Occupied' : allVac ? 'Vacant' : 'Partial' };
      }));
      closeLC();
    } catch (err) {
      setLcError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLcSubmitting(false);
    }
  };

  const loadPdfJs = () => new Promise((resolve, reject) => {
    if (window.pdfjsLib) { resolve(window.pdfjsLib); return; }
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    script.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc =
        'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
      resolve(window.pdfjsLib);
    };
    script.onerror = reject;
    document.head.appendChild(script);
  });

  const pdfToImages = async (file) => {
    const lib = await loadPdfJs();
    const buf = await file.arrayBuffer();
    const pdf = await lib.getDocument({ data: buf }).promise;
    const pages = [];
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const vp = page.getViewport({ scale: 1.5 });
      const canvas = document.createElement('canvas');
      canvas.width = vp.width;
      canvas.height = vp.height;
      await page.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise;
      pages.push({
        file: null,
        originalFile: file,
        preview: canvas.toDataURL('image/jpeg', 0.85),
        mediaType: 'image/jpeg',
        name: pdf.numPages > 1 ? `${file.name} — page ${i}` : file.name,
      });
    }
    return pages;
  };

  const compressImageFile = (file) => new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { naturalWidth: w, naturalHeight: h } = img;
      const maxDim = 2400;
      if (w > maxDim || h > maxDim) { const s = maxDim / Math.max(w, h); w = Math.round(w * s); h = Math.round(h * s); }
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      const tryQ = (q) => {
        canvas.toBlob((blob) => {
          if (!blob) { resolve(file); return; }
          if (blob.size <= MAX_LEASE_SIZE || q <= 0.3) {
            resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' }));
          } else { tryQ(q - 0.15); }
        }, 'image/jpeg', q);
      };
      tryQ(0.7);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });

  const loadJsPdf = () => new Promise((resolve, reject) => {
    if (window.jspdf?.jsPDF) { resolve(window.jspdf.jsPDF); return; }
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
    s.onload = () => resolve(window.jspdf.jsPDF);
    s.onerror = reject;
    document.head.appendChild(s);
  });

  const compressPdfPages = async (originalFile, pagePreviews) => {
    try {
      const JsPDF = await loadJsPdf();
      let doc = null;
      for (const dataUrl of pagePreviews) {
        const img = new Image();
        await new Promise(res => { img.onload = res; img.src = dataUrl; });
        const w = img.naturalWidth, h = img.naturalHeight;
        if (!doc) {
          doc = new JsPDF({ unit: 'px', format: [w, h], compress: true, orientation: w > h ? 'l' : 'p' });
        } else {
          doc.addPage([w, h], w > h ? 'l' : 'p');
        }
        doc.addImage(dataUrl, 'JPEG', 0, 0, w, h, undefined, 'FAST');
      }
      if (!doc) return originalFile;
      return new File([doc.output('arraybuffer')], originalFile.name, { type: 'application/pdf' });
    } catch { return originalFile; }
  };

  const handleAiFiles = async (fileList) => {
    const toAdd = [];
    const hasPdf = Array.from(fileList).some(f => f.type === 'application/pdf');
    if (hasPdf) setLcAiConverting(true);
    for (const file of Array.from(fileList)) {
      if (!['image/jpeg', 'image/png', 'application/pdf'].includes(file.type)) continue;
      if (file.type === 'application/pdf') {
        try {
          const pages = await pdfToImages(file);
          toAdd.push(...pages);
        } catch {
          const preview = await new Promise(res => {
            const reader = new FileReader();
            reader.onload = e => res(e.target.result);
            reader.readAsDataURL(file);
          });
          toAdd.push({ file, originalFile: null, preview, mediaType: file.type, name: file.name });
        }
      } else {
        const preview = await new Promise(res => {
          const reader = new FileReader();
          reader.onload = e => res(e.target.result);
          reader.readAsDataURL(file);
        });
        toAdd.push({ file, originalFile: null, preview, mediaType: file.type, name: file.name });
      }
    }
    if (hasPdf) setLcAiConverting(false);
    setLcAiFiles(prev => [...prev, ...toAdd]);
  };

  const handleAiExtract = async () => {
    if (!lcAiFiles.length) return;
    setLcStep('ai-extracting');
    setLcAiError('');
    try {
      const pages = lcAiFiles.map(f => ({
        data: f.preview.split(',')[1],
        media_type: f.mediaType
      }));
      const r = await fetch(`${BASE_URL}/ExtractLeaseData`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(pages)
      });
      const text = await r.text();
      if (!r.ok) {
        console.error('ExtractLeaseData error:', r.status, text);
        let errMsg = `Extraction failed (${r.status}). Please try again.`;
        try { const e = JSON.parse(text); errMsg = e.detail || e.message || errMsg; } catch {}
        throw new Error(errMsg);
      }
      let data = {};
      try {
        const parsed = JSON.parse(text);
        data = typeof parsed === 'string' ? (() => { try { return JSON.parse(parsed); } catch { return {}; } })() : (parsed || {});
      } catch { data = {}; }
      console.log('ExtractLeaseData response:', data);

      // Case-insensitive field lookup covering many naming conventions
      const pick = (...keys) => {
        for (const key of keys) {
          if (data[key] !== undefined && data[key] !== null && String(data[key]).trim() !== '') return String(data[key]).trim();
          const lk = key.toLowerCase();
          const found = Object.keys(data).find(k => k.toLowerCase() === lk);
          if (found && data[found] !== undefined && data[found] !== null && String(data[found]).trim() !== '') return String(data[found]).trim();
        }
        return '';
      };

      const extractedProp = pick('property_name', 'property_address', 'address');
      const extractedUnit = pick('unit_number', 'unit', 'apartment', 'apt');
      if (extractedProp) {
        const matchedProp = properties.find(p =>
          p.name?.toLowerCase().includes(extractedProp.toLowerCase()) ||
          p.address?.toLowerCase().includes(extractedProp.toLowerCase())
        );
        if (matchedProp) {
          setLcPropertyId(String(matchedProp.id));
          const isMulti = parseInt(matchedProp.numberOfUnits) > 1;
          if (!isMulti) {
            setLcDoorId(matchedProp.units[0]?.doorId || '');
          } else if (extractedUnit) {
            const mu = matchedProp.units.find(u =>
              String(u.unitNumber).toLowerCase() === String(extractedUnit).toLowerCase()
            );
            if (mu) setLcDoorId(mu.doorId || '');
          }
        }
      }
      setLcAiForm({
        tenantName: pick('tenant_name', 'full_name', 'name', 'Tenant_Name', 'primary_tenant'),
        phone: pick('phone', 'phone_number', 'Phone_Number', 'Phone', 'tenant_phone', 'mobile', 'cell', 'contact_phone'),
        email: pick('email', 'Email', 'tenant_email', 'email_address', 'contact_email', 'Tenant_Email'),
        rent: pick('monthly_rent', 'rent', 'Rent', 'rental_amount', 'monthly_payment'),
        deposit: pick('security_deposit', 'deposit', 'Deposit', 'security_dep'),
        leaseStart: toISO(pick('lease_start_date', 'start_date', 'Start_Date', 'lease_start', 'commencement_date')) || todayISO,
        leaseEnd: toISO(pick('lease_end_date', 'end_date', 'End_Date', 'lease_end', 'expiration_date')) || nextYearISO,
        rentDue: pick('rent_due_date', 'rent_due', 'Rent_Due', 'due_date', 'payment_due_day') || '1',
        leaseType: pick('lease_type', 'lease_term', 'Lease_Type') || (pick('month_to_month') === 'true' ? 'Month-to-Month' : 'Fixed Term'),
        insuranceStart: toISO(pick('insurance_effective_date', 'insurance_start_date', 'insurance_start', 'Insurance_Start', 'renters_insurance_start', 'policy_start_date', 'policy_start', 'effective_date')) || '',
        insuranceEnd: toISO(pick('insurance_expiration_date', 'insurance_end_date', 'insurance_end', 'Insurance_End', 'renters_insurance_end', 'policy_end_date', 'policy_end', 'policy_expiration', 'expiration_date')) || '',
      });
      // Extract fees from AI response
      const rawFees = data.fees || data.additional_fees || data.other_fees || data.fee_schedule || [];
      if (Array.isArray(rawFees) && rawFees.length > 0) {
        const parsedFees = rawFees.map(f => ({
          name: String(f.name || f.fee_name || f.type || f.fee_type || '').trim(),
          amount: String(f.amount || f.fee_amount || f.value || '').trim(),
        })).filter(f => f.name);
        setLcAiFees(parsedFees.length > 0 ? parsedFees : [{ name: '', amount: '' }]);
      } else {
        setLcAiFees([{ name: '', amount: '' }]);
      }
      setLcStep('ai-review');
    } catch (e) {
      setLcAiError(e.message || 'Could not extract lease data. Please try again.');
      setLcStep('ai-upload');
    }
  };

  const handleAiImport = async () => {
    if (!lcPropertyId || (lcIsMulti && !lcDoorId) || !lcAiForm.tenantName.trim() || !lcAiForm.rent) {
      setLcAiError('Please fill in all required fields and select a property/unit.');
      return;
    }
    const doorId = lcIsMulti ? lcDoorId : (lcProperty?.units[0]?.doorId || lcDoorId);
    if (!doorId) { setLcAiError('Could not determine unit. Please select a unit.'); return; }
    setLcSubmitting(true);
    setLcAiError('');
    try {
      // Step 1 — Add tenant
      const tenantParams2 = new URLSearchParams({ Full_Name: lcAiForm.tenantName, Phone_Number: lcAiForm.phone, Door_id: doorId });
      const tenantRes = await fetch(`${BASE_URL}/AddTenant?${tenantParams2}`, { method: 'POST' });
      if (!tenantRes.ok) throw new Error('Failed to add tenant');

      // Step 2 — Add lease
      const leaseParams2 = new URLSearchParams({
        Rent: parseFloat(lcAiForm.rent) || 0, Deposit: parseFloat(lcAiForm.deposit) || 0,
        Start_Date: lcAiForm.leaseStart || '', End_Date: lcAiForm.leaseEnd || '',
        Rent_Due: lcAiForm.rentDue || '1', MonthToMonth: lcAiForm.leaseType === 'Month-to-Month',
        Door_id: doorId
      });
      const leaseRes = await fetch(`${BASE_URL}/AddLease?${leaseParams2}`, { method: 'POST' });
      if (!leaseRes.ok) throw new Error('Failed to add lease');

      const validAiFees = lcAiFees.filter(f => f.name.trim() && parseFloat(f.amount) > 0);
      await Promise.all(validAiFees.map(f =>
        fetch(`${BASE_URL}/AddFee?fee_type=${encodeURIComponent(f.name.trim())}&amount=${parseFloat(f.amount)}&door_id=${doorId}`, { method: 'POST', headers: { accept: 'application/json' } })
      ));

      // Step 3 — Get lease_id then upload doc
      if (lcAiSaveDoc && lcAiFiles.length > 0) {
        const entry = lcAiFiles[0];
        let uploadFile = entry.originalFile || entry.file;
        if (uploadFile) {
          // Compress if over 10 MB
          if (uploadFile.size > MAX_LEASE_SIZE) {
            if (uploadFile.type === 'application/pdf') {
              const pagePreviews = lcAiFiles.filter(f => f.originalFile === uploadFile).map(f => f.preview);
              uploadFile = await compressPdfPages(uploadFile, pagePreviews.length ? pagePreviews : [entry.preview]);
            } else {
              uploadFile = await compressImageFile(uploadFile);
            }
          }
          const leaseInfoRes = await fetch(`${BASE_URL}/Lease&TenantsByDoor?door_id=${doorId}`);
          const leaseInfoData = leaseInfoRes.ok ? await leaseInfoRes.json() : null;
          const leaseId = leaseInfoData?.tenants?.[0]?.lease_id || null;
          const fd = new FormData();
          fd.append('file', uploadFile);
          const uploadParams = new URLSearchParams({ door_id: doorId });
          if (leaseId) uploadParams.set('lease_id', leaseId);
          if (pmId) uploadParams.set('pm_id', pmId);
          await fetch(`${BASE_URL}/UploadLease?${uploadParams}`, { method: 'POST', body: fd });
        }
      }

      // Step 4 — Update door status
      await fetch(`${BASE_URL}/UpdateDoorStatus?door_id=${doorId}&status=Occupied`, { method: 'PUT' });
      if ((lcAiForm.insuranceStart || lcAiForm.insuranceEnd) && lcProperty?.name && userEmail) {
        const toMMDDYYYY = (d) => { if (!d) return ''; const [y, m, day] = d.split('-'); return `${m}/${day}/${y}`; };
        const ip = new URLSearchParams({ property_name: lcProperty.name, email: userEmail });
        if (lcAiForm.insuranceStart) ip.set('insurance_start', toMMDDYYYY(lcAiForm.insuranceStart));
        if (lcAiForm.insuranceEnd) ip.set('insurance_end', toMMDDYYYY(lcAiForm.insuranceEnd));
        await fetch(`${BASE_URL}/UpdateInsuranceDates?${ip}`, { method: 'PUT' });
      }
      setProperties(prev => prev.map(p => {
        if (String(p.id) !== String(lcPropertyId)) return p;
        const updatedUnits = p.units.map(u => u.doorId === doorId ? { ...u, status: 'Occupied' } : u);
        const allOcc = updatedUnits.every(u => u.status === 'Occupied');
        const allVac = updatedUnits.every(u => u.status !== 'Occupied');
        return { ...p, units: updatedUnits, status: allOcc ? 'Occupied' : allVac ? 'Vacant' : 'Partial' };
      }));
      closeLC();
    } catch (err) {
      setLcAiError(err.message || 'Import failed. Please try again.');
    } finally {
      setLcSubmitting(false);
    }
  };

  return (
    <div className="home-page">
      <Sidebar initials={initials} />
      <div className="main-content">
        <Navbar title="Properties" />

        <div className="props-container">

          {demoMode && (
            <div className="demo-mode-banner">
              <span className="demo-mode-icon">📊</span>
              Sample data — add your first property to replace this
            </div>
          )}

          {/* HEADER CARD: title + inline stats */}
          <div className="props-header-card">
            <div className="props-header-left">
              <h1 className="props-title">Properties</h1>
              <p className="props-subtitle">Manage and monitor all your properties</p>
            </div>
            <div id="props-stat-cards" className="props-inline-stats">
              <div className="props-istat">
                <div className="props-istat-icon props-istat-green"><FaBuilding /></div>
                <div className="props-istat-text">
                  <div className="props-istat-label">PROPERTIES</div>
                  <div className="props-istat-val">{totalProperties}</div>
                </div>
              </div>
              <div className="props-istat">
                <div className="props-istat-icon props-istat-blue"><FaDollarSign /></div>
                <div className="props-istat-text">
                  <div className="props-istat-label">UNITS</div>
                  <div className="props-istat-val">{totalUnits}</div>
                </div>
              </div>
              <div className="props-istat">
                <div className="props-istat-icon props-istat-green2"><FaHome /></div>
                <div className="props-istat-text">
                  <div className="props-istat-label">OCCUPIED</div>
                  <div className="props-istat-val">{occupiedCount}</div>
                </div>
              </div>
              <div className="props-istat">
                <div className="props-istat-icon props-istat-orange"><FaBuilding /></div>
                <div className="props-istat-text">
                  <div className="props-istat-label">VACANT</div>
                  <div className="props-istat-val props-istat-red">{vacantCount}</div>
                </div>
              </div>
            </div>
          </div>

          {/* ACTION BAR */}
          <div className="props-action-bar">
            <div className="props-search-filter-row">
              <div className="props-search-wrap">
                <FaSearch className="props-search-icon" />
                <input
                  className="props-search-input"
                  placeholder="Search properties..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
              </div>
              <button
                className={`props-filter-btn ${showFilter ? 'props-filter-btn-active' : ''}`}
                onClick={() => setShowFilter(v => !v)}
              >
                <FaSlidersH />
                Filters
                {activeFilterCount > 0 && <span className="props-filter-badge">{activeFilterCount}</span>}
              </button>
            </div>
            <div id="props-action-btns" className="props-action-btns">
              <button className="props-btn-lease" onClick={openLC}>
                <FaBuilding /> Leasing Center
              </button>
              <button className="props-btn-projection" onClick={() => navigate('/projection')}>
                <FaChartBar /> Projection
              </button>
              <button className="props-btn-add" onClick={() => navigate('/properties/add')}>
                <FaPlus /> Add Property
              </button>
            </div>
          </div>

          {/* FILTER DRAWER */}
          {showFilter && (
            <div className="props-drawer-backdrop" onClick={() => setShowFilter(false)} />
          )}
          <div id="props-filter-drawer" className={`props-filter-drawer ${showFilter ? 'props-filter-drawer-open' : ''}`}>
            <div className="props-drawer-header">
              <h3>Filters</h3>
              <button className="props-drawer-close" onClick={() => setShowFilter(false)}><FaTimes /></button>
            </div>
            <div className="props-drawer-body">

              {/* Search by name */}
              <div className="props-drawer-section">
                <div className="props-drawer-section-title">Search</div>
                <div className="props-drawer-search-wrap">
                  <FaSearch className="props-drawer-search-icon" />
                  <input
                    className="props-drawer-input"
                    placeholder="Property name..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>

              {/* Address / Region */}
              <div className="props-drawer-section">
                <div className="props-drawer-section-title">Address / Region</div>
                <div className="props-drawer-search-wrap">
                  <FaMapMarkerAlt className="props-drawer-search-icon" />
                  <input
                    className="props-drawer-input"
                    placeholder="Street, city, state, zip..."
                    value={filterAddress}
                    onChange={e => setFilterAddress(e.target.value)}
                  />
                </div>
                {filterAddress && (
                  <button className="props-drawer-clear-link" onClick={() => setFilterAddress('')}>Clear</button>
                )}
              </div>

              {/* Property Type */}
              <div className="props-drawer-section">
                <div className="props-drawer-section-title">Property Type</div>
                {['All', 'Single Family', 'Multi Family'].map(t => (
                  <label key={t} className="props-drawer-radio-row">
                    <span>{t}</span>
                    <input
                      type="radio"
                      name="propType"
                      checked={filterType === t}
                      onChange={() => setFilterType(t)}
                      className="props-drawer-radio"
                    />
                  </label>
                ))}
              </div>

              {/* Status */}
              <div className="props-drawer-section">
                <div className="props-drawer-section-title">Status</div>
                {['All', 'Occupied', 'Vacant', 'Partial'].map(s => (
                  <label key={s} className="props-drawer-radio-row">
                    <span>{s}</span>
                    <input
                      type="radio"
                      name="propStatus"
                      checked={filterStatus === s}
                      onChange={() => setFilterStatus(s)}
                      className="props-drawer-radio"
                    />
                  </label>
                ))}
              </div>

            </div>
            {activeFilterCount > 0 && (
              <div className="props-drawer-footer">
                <button className="props-drawer-reset-btn" onClick={resetFilters}>
                  Reset All Filters
                </button>
              </div>
            )}
          </div>

          {/* PROPERTY CARDS */}
          {filteredProperties.length > 0 ? (
            <div id="props-list" className="props-grid">
              {filteredProperties.map((property, propIdx) => {
                const isMulti = parseInt(property.numberOfUnits) > 1;
                const isExpanded = expandedPropertyId === property.id;
                return (
                    <div
                      key={property.id}
                      id={propIdx === 0 ? 'props-first-card' : undefined}
                      className={`props-card
                        ${property.status === 'Occupied' ? 'props-card-green' : property.status === 'Partial' ? 'props-card-yellow' : 'props-card-orange'}
                        ${isExpanded ? 'props-card-active' : ''}`}
                    >
                    <div className="props-card-main">
                      <div className={`props-card-icon-box ${isMulti ? 'props-icon-sf' : 'props-icon-sf'} ${property.status === 'Occupied' ? 'props-icon-green' : property.status === 'Partial' ? 'props-icon-yellow' : 'props-icon-orange'}`}>
                        {isMulti ? <FaCity /> : <FaHome />}
                      </div>
                      <div className="props-card-info">
                        <div className="props-card-name">{property.name}</div>
                        <div className="props-card-addr">
                          <FaMapMarkerAlt className="props-pin" />
                          {property.address}
                        </div>
                        {!isMulti && (
                          <div className={`props-card-status ${property.status === 'Occupied' ? 'props-st-occ' : 'props-st-vac'}`}>
                            {property.status?.toUpperCase()}
                          </div>
                        )}
                        <button
                          className="props-view-btn"
                          onClick={() => setExpandedPropertyId(isExpanded ? null : property.id)}
                        >
                          VIEW DETAILS {isExpanded ? <FaChevronUp style={{ fontSize: 10 }} /> : <FaChevronDown style={{ fontSize: 10 }} />}
                        </button>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="props-expanded">
                        {isMulti ? (
                          <>
                            {property.units.map((unit, i) => (
                              <div key={i} className="props-exp-row" onClick={() => navigate(`/properties/${property.id}?door=${unit.doorId}`)}>
                                <div className="props-exp-icon-wrap props-exp-icon-green"><FaHome /></div>
                                <span className="props-exp-label">
                                  {unit.unitNumber ? `Unit ${unit.unitNumber}` : `Unit ${String.fromCharCode(65 + i)}`}
                                </span>
                                <span className={`props-exp-status ${unit.status === 'Occupied' ? 'props-st-occ' : 'props-st-vac'}`}>
                                  {unit.status?.toUpperCase()}
                                </span>
                                <FaChevronRight className="props-exp-arrow" />
                              </div>
                            ))}
                            <div className="props-exp-row" onClick={() => navigate(`/reports/${property.id}`)}>
                              <div className="props-exp-icon-wrap props-exp-icon-purple"><FaChartBar /></div>
                              <span className="props-exp-label">Reports</span>
                              <FaChevronRight className="props-exp-arrow" />
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="props-exp-row" onClick={() => navigate(`/properties/${property.id}`)}>
                              <div className="props-exp-icon-wrap props-exp-icon-green"><FaHome /></div>
                              <span className="props-exp-label">Property Details</span>
                              <FaChevronRight className="props-exp-arrow" />
                            </div>
                            <div className="props-exp-row" onClick={() => navigate(`/reports/${property.id}`)}>
                              <div className="props-exp-icon-wrap props-exp-icon-purple"><FaChartBar /></div>
                              <span className="props-exp-label">Reports</span>
                              <FaChevronRight className="props-exp-arrow" />
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="no-properties">
              <p>No properties found</p>
              <button onClick={() => navigate('/properties/add')}>Add Your First Property</button>
            </div>
          )}

        </div>
      </div>

      {/* ======= LEASING CENTER MODAL ======= */}
      {showLC && (
        <div className="lc-overlay">
          <div className="lc-modal" onClick={e => e.stopPropagation()}>

            {/* ---- STEP 1: LEASING CENTER ---- */}
            {lcStep === 'center' && (
              <>
                <div className="lc-header">
                  <div className="lc-header-left" />
                  <h2 className="lc-title">Leasing Center</h2>
                  <button className="lc-close-btn" onClick={closeLC}><FaTimes /></button>
                </div>
                <div className="lc-body">

                  {/* AI Import card */}
                  <div className="lc-ai-card" onClick={() => { setLcStep('ai-upload'); setLcAiFiles([]); setLcAiError(''); setLcAiSaveDoc(false); }}>
                    <div className="lc-ai-icon"><FaMagic /></div>
                    <div className="lc-ai-text">
                      <div className="lc-ai-title">Import Lease</div>
                      <div className="lc-ai-sub">Scan a lease document to auto-fill this form</div>
                    </div>
                    <FaChevronRight className="lc-ai-arrow" />
                  </div>

                  <div className="lc-or-divider"><span>OR</span></div>

                  <label className="lc-label">Property Name</label>
                  <select className="lc-select" value={lcPropertyId} onChange={e => handleLcPropertyChange(e.target.value)}>
                    <option value="">Select property…</option>
                    {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>

                  {lcProperty && (
                    <>
                      <label className="lc-label" style={{ marginTop: 14 }}>Property Address</label>
                      <input className="lc-input lc-input-readonly" readOnly value={lcProperty.address || ''} />

                      {lcIsMulti && (
                        <>
                          <label className="lc-label" style={{ marginTop: 14 }}>Unit</label>
                          <select className="lc-select" value={lcDoorId} onChange={e => handleLcUnitChange(e.target.value)}>
                            <option value="">Select unit…</option>
                            {lcProperty.units.map((u, i) => (
                              <option key={u.doorId} value={u.doorId}>
                                {u.unitNumber ? `Unit ${u.unitNumber}` : `Unit ${String.fromCharCode(65 + i)}`} — {u.status}
                              </option>
                            ))}
                          </select>
                        </>
                      )}

                      {(!lcIsMulti || lcDoorId) && (() => {
                        const actualStatus = lcIsMulti
                          ? lcProperty?.units.find(u => u.doorId === lcDoorId)?.status
                          : lcProperty?.units[0]?.status;
                        const wasOccupied = actualStatus === 'Occupied';
                        const switchingToVacant = wasOccupied && lcUnitStatus === 'Vacant';
                        return (
                          <div className="lc-status-box">
                            <div className="lc-status-title">Unit Status</div>
                            <div className="lc-status-row">
                              <button
                                className={`lc-status-opt ${lcUnitStatus === 'Occupied' ? 'lc-status-active-green' : ''}`}
                                onClick={() => setLcUnitStatus('Occupied')}
                              >
                                {lcUnitStatus === 'Occupied' && <span className="lc-status-check lc-check-green"><FaCheck /></span>}
                                <span>Occupied</span>
                              </button>
                              <button
                                className={`lc-status-opt ${lcUnitStatus === 'Vacant' ? 'lc-status-active-gray' : ''}`}
                                onClick={() => setLcUnitStatus('Vacant')}
                              >
                                {lcUnitStatus === 'Vacant' && <span className="lc-status-check lc-check-gray"><FaCheck /></span>}
                                <span>Vacant</span>
                              </button>
                            </div>
                            {switchingToVacant && (
                              <button className="lc-add-lease-btn" style={{ background: '#dc2626' }} onClick={handleMarkVacant} disabled={lcVacantSaving}>
                                {lcVacantSaving ? 'Saving…' : 'Save — Mark as Vacant'}
                              </button>
                            )}
                            {!wasOccupied && lcUnitStatus === 'Vacant' && (
                              <button className="lc-add-lease-btn" onClick={() => setLcStep('form')}>
                                <FaPlus /> Add Lease &amp; Tenant
                              </button>
                            )}
                          </div>
                        );
                      })()}
                    </>
                  )}
                </div>
              </>
            )}

            {/* ---- STEP 2: ADD LEASE & TENANT ---- */}
            {lcStep === 'form' && (
              <>
                <div className="lc-header">
                  <button className="lc-back-btn" onClick={() => setLcStep('center')}>Cancel</button>
                  <h2 className="lc-title">Add Lease &amp; Tenant</h2>
                  <div className="lc-header-right" />
                </div>
                <div className="lc-body lc-form-body">

                  <label className="lc-label">Tenant Name <span className="lc-req">*</span></label>
                  <input className="lc-input" placeholder="Tenant Name" value={lcForm.tenantName}
                    onChange={e => setLcForm(f => ({ ...f, tenantName: e.target.value }))} />

                  <label className="lc-label" style={{ marginTop: 14 }}>Phone Number <span className="lc-req">*</span></label>
                  <input className="lc-input" placeholder="Phone Number" type="tel" value={lcForm.phoneNumber}
                    onChange={e => setLcForm(f => ({ ...f, phoneNumber: formatPhone(e.target.value) }))} />

                  <div className="lc-two-col-box" style={{ marginTop: 14 }}>
                    <div>
                      <label className="lc-label-sm">Deposit</label>
                      <input className="lc-input lc-input-inner" placeholder="0.00" type="number" min="0" value={lcForm.deposit}
                        onChange={e => setLcForm(f => ({ ...f, deposit: e.target.value }))} />
                    </div>
                    <div>
                      <label className="lc-label-sm">Rent <span className="lc-req">*</span></label>
                      <input className="lc-input lc-input-inner" placeholder="0.00" type="number" min="0" value={lcForm.rent}
                        onChange={e => setLcForm(f => ({ ...f, rent: e.target.value }))} />
                    </div>
                  </div>

                  <div className="lc-two-col-box" style={{ marginTop: 14 }}>
                    <div>
                      <label className="lc-label-sm">Lease Start Date</label>
                      <input className="lc-input lc-input-inner" type="date" value={lcForm.leaseStartDate}
                        onChange={e => { const v = e.target.value; setLcForm(f => { const d = new Date(v); d.setFullYear(d.getFullYear() + 1); return { ...f, leaseStartDate: v, leaseEndDate: d.toISOString().slice(0, 10) }; }); }} />
                    </div>
                    <div>
                      <label className="lc-label-sm">Lease End Date</label>
                      <input className="lc-input lc-input-inner" type="date" value={lcForm.leaseEndDate}
                        onChange={e => setLcForm(f => ({ ...f, leaseEndDate: e.target.value }))} />
                    </div>
                  </div>

                  <label className="lc-label" style={{ marginTop: 14 }}>Rent Due Day</label>
                  <select className="lc-select" value={lcForm.rentDueDay}
                    onChange={e => setLcForm(f => ({ ...f, rentDueDay: e.target.value }))}>
                    {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                      <option key={d} value={String(d)}>{d}</option>
                    ))}
                  </select>

                  <label className="lc-label" style={{ marginTop: 14 }}>Lease Term</label>
                  <select className="lc-select" value={lcForm.leaseType}
                    onChange={e => setLcForm(f => ({ ...f, leaseType: e.target.value }))}>
                    {['Fixed Term', 'Month-to-Month', 'Other'].map(t => <option key={t} value={t}>{t}</option>)}
                  </select>

                  <div className="lc-fees-section" style={{ marginTop: 14 }}>
                    <div className="lc-fees-title">Additional Fees <span className="lc-optional">(Optional)</span></div>
                    <div className="lc-fees-box">
                      {lcFees.map((fee, i) => (
                        <div key={i} className="lc-fee-row">
                          <input className="lc-fee-name" placeholder="Fee name" value={fee.name}
                            onChange={e => setLcFees(prev => prev.map((f, j) => j === i ? { ...f, name: e.target.value } : f))} />
                          <input className="lc-fee-amount" placeholder="Amount" type="number" min="0" value={fee.amount}
                            onChange={e => setLcFees(prev => prev.map((f, j) => j === i ? { ...f, amount: e.target.value } : f))} />
                          {lcFees.length > 1 && (
                            <button className="lc-fee-remove" onClick={() => setLcFees(prev => prev.filter((_, j) => j !== i))}><FaTimes /></button>
                          )}
                        </div>
                      ))}
                      <button className="lc-add-fee-btn" onClick={() => setLcFees(prev => [...prev, { name: '', amount: '' }])}>
                        <FaPlus /> Add Fee
                      </button>
                      <p className="lc-fees-hint">Add any additional monthly fees like pet fees, parking, or storage.</p>
                    </div>
                  </div>

                  {lcError && <div className="lc-error">{lcError}</div>}

                  <button
                    className="lc-save-btn"
                    onClick={submitLease}
                    disabled={lcSubmitting || !lcForm.tenantName.trim() || !lcForm.rent}
                  >
                    {lcSubmitting ? 'Saving…' : 'Save'}
                  </button>
                  <p className="lc-required-note">* Required fields</p>
                </div>
              </>
            )}

            {/* ---- AI: UPLOAD STEP ---- */}
            {lcStep === 'ai-upload' && (
              <>
                <div className="lc-header">
                  <button className="lc-back-btn" onClick={() => setLcStep('center')}>← Back</button>
                  <h2 className="lc-title">Import Lease</h2>
                  <button className="lc-close-btn" onClick={closeLC}><FaTimes /></button>
                </div>
                <div className="lc-body">
                  <p className="lc-ai-step-intro">Upload your lease document and the details will be extracted automatically.</p>
                  <input ref={lcAiFileRef} type="file" accept="image/jpeg,image/png,application/pdf" multiple style={{display:'none'}} onChange={e => handleAiFiles(e.target.files)} />
                  <div className="lc-ai-drop-zone"
                    onClick={() => lcAiFileRef.current?.click()}
                    onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('lc-ai-drag-over'); }}
                    onDragLeave={e => e.currentTarget.classList.remove('lc-ai-drag-over')}
                    onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove('lc-ai-drag-over'); handleAiFiles(e.dataTransfer.files); }}>
                    <FaUpload className="lc-ai-dz-icon" />
                    <p className="lc-ai-dz-title">Drop files or <span className="lc-ai-dz-link">browse</span></p>
                    <p className="lc-ai-dz-hint">JPEG, PNG, PDF — multiple pages OK</p>
                  </div>
                  {lcAiFiles.length > 0 && (
                    <div className="lc-ai-previews">
                      {lcAiFiles.map((f, i) => (
                        <div key={i} className="lc-ai-preview-card">
                          {f.mediaType === 'application/pdf'
                            ? <div className="lc-ai-preview-pdf"><FaFilePdf className="lc-ai-pdf-icon" /></div>
                            : <img src={f.preview} alt={`p${i+1}`} className="lc-ai-preview-img" />}
                          <span className="lc-ai-preview-name">{f.name.length > 14 ? f.name.slice(0,12)+'…' : f.name}</span>
                          <button className="lc-ai-preview-rm" onClick={e => { e.stopPropagation(); setLcAiFiles(p=>p.filter((_,j)=>j!==i)); }}><FaTimes /></button>
                        </div>
                      ))}
                    </div>
                  )}
                  {lcAiConverting && (
                    <div style={{display:'flex',alignItems:'center',gap:8,fontSize:13,color:'#7c3aed',margin:'4px 0'}}>
                      <FaSpinner className="lc-spin" style={{fontSize:14}} /> Converting PDF pages to images…
                    </div>
                  )}
                  {lcAiError && <div className="lc-error" style={{marginTop:12}}>{lcAiError}</div>}
                  <button className="lc-save-btn" onClick={handleAiExtract} disabled={!lcAiFiles.length || lcAiConverting}
                    style={{display:'inline-flex',alignItems:'center',gap:8,justifyContent:'center'}}>
                    <FaMagic /> Extract
                  </button>
                </div>
              </>
            )}

            {/* ---- AI: EXTRACTING ---- */}
            {lcStep === 'ai-extracting' && (
              <>
                <div className="lc-header">
                  <div className="lc-header-left" />
                  <h2 className="lc-title">Extracting…</h2>
                  <div className="lc-header-right" />
                </div>
                <div className="lc-body">
                  <div className="lc-ai-extracting">
                    <FaSpinner className="lc-spin" />
                    <p className="lc-ai-ext-title">Reading your lease…</p>
                    <p className="lc-ai-ext-sub">This takes a few seconds…</p>
                  </div>
                </div>
              </>
            )}

            {/* ---- AI: REVIEW STEP ---- */}
            {lcStep === 'ai-review' && (
              <>
                <div className="lc-header">
                  <button className="lc-back-btn" onClick={() => setLcStep('ai-upload')}>← Back</button>
                  <h2 className="lc-title">Review Details</h2>
                  <button className="lc-close-btn" onClick={closeLC}><FaTimes /></button>
                </div>
                <div className="lc-body lc-form-body">
                  <p className="lc-ai-step-intro">Review and edit the extracted information.</p>
                  {lcAiError && <div className="lc-error" style={{marginBottom:8}}>{lcAiError}</div>}

                  <label className="lc-label">Tenant Name <span className="lc-req">*</span></label>
                  <input className="lc-input" placeholder="Full name" value={lcAiForm.tenantName}
                    onChange={e => setLcAiForm(f => ({ ...f, tenantName: e.target.value }))} />

                  <label className="lc-label" style={{marginTop:12}}>Phone Number</label>
                  <input className="lc-input" placeholder="(555) 000-0000" type="tel" value={lcAiForm.phone}
                    onChange={e => setLcAiForm(f => ({ ...f, phone: e.target.value }))} />

                  <label className="lc-label" style={{marginTop:12}}>Email</label>
                  <input className="lc-input" placeholder="tenant@email.com" type="email" value={lcAiForm.email}
                    onChange={e => setLcAiForm(f => ({ ...f, email: e.target.value }))} />

                  <div className="lc-two-col-box" style={{marginTop:12}}>
                    <div>
                      <label className="lc-label-sm">Deposit</label>
                      <input className="lc-input lc-input-inner" placeholder="0.00" type="number" min="0" value={lcAiForm.deposit}
                        onChange={e => setLcAiForm(f => ({ ...f, deposit: e.target.value }))} />
                    </div>
                    <div>
                      <label className="lc-label-sm">Rent <span className="lc-req">*</span></label>
                      <input className="lc-input lc-input-inner" placeholder="0.00" type="number" min="0" value={lcAiForm.rent}
                        onChange={e => setLcAiForm(f => ({ ...f, rent: e.target.value }))} />
                    </div>
                  </div>

                  <div className="lc-two-col-box" style={{marginTop:12}}>
                    <div>
                      <label className="lc-label-sm">Lease Start</label>
                      <input className="lc-input lc-input-inner" type="date" value={lcAiForm.leaseStart}
                        onChange={e => setLcAiForm(f => ({ ...f, leaseStart: e.target.value }))} />
                    </div>
                    <div>
                      <label className="lc-label-sm">Lease End</label>
                      <input className="lc-input lc-input-inner" type="date" value={lcAiForm.leaseEnd}
                        onChange={e => setLcAiForm(f => ({ ...f, leaseEnd: e.target.value }))} />
                    </div>
                  </div>

                  <div className="lc-two-col-box" style={{marginTop:12}}>
                    <div>
                      <label className="lc-label-sm">Insurance Start <span className="lc-optional">(optional)</span></label>
                      <div className="lc-date-clear-wrap">
                        <input className="lc-input lc-input-inner" type="date" value={lcAiForm.insuranceStart}
                          onChange={e => setLcAiForm(f => ({ ...f, insuranceStart: e.target.value }))} />
                        {lcAiForm.insuranceStart && (
                          <button className="lc-date-clear-btn" onClick={() => setLcAiForm(f => ({ ...f, insuranceStart: '' }))} type="button">×</button>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="lc-label-sm">Insurance End <span className="lc-optional">(optional)</span></label>
                      <div className="lc-date-clear-wrap">
                        <input className="lc-input lc-input-inner" type="date" value={lcAiForm.insuranceEnd}
                          onChange={e => setLcAiForm(f => ({ ...f, insuranceEnd: e.target.value }))} />
                        {lcAiForm.insuranceEnd && (
                          <button className="lc-date-clear-btn" onClick={() => setLcAiForm(f => ({ ...f, insuranceEnd: '' }))} type="button">×</button>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="lc-two-col-box" style={{marginTop:12}}>
                    <div>
                      <label className="lc-label-sm">Rent Due Day</label>
                      <select className="lc-select lc-select-sm" value={lcAiForm.rentDue}
                        onChange={e => setLcAiForm(f => ({ ...f, rentDue: e.target.value }))}>
                        {Array.from({length:31},(_,i)=>i+1).map(d => <option key={d} value={String(d)}>Day {d}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="lc-label-sm">Lease Type</label>
                      <select className="lc-select lc-select-sm" value={lcAiForm.leaseType}
                        onChange={e => setLcAiForm(f => ({ ...f, leaseType: e.target.value }))}>
                        {['Fixed Term', 'Month-to-Month', 'Other'].map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                  </div>

                  <div className="lc-prop-select-card" style={{marginTop:14}}>
                    <label className="lc-label-sm">Property <span className="lc-req">*</span></label>
                    <select className="lc-select" value={lcPropertyId} onChange={e => handleLcPropertyChange(e.target.value)}>
                      <option value="">Select property…</option>
                      {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                    {lcIsMulti && lcProperty && (
                      <div style={{marginTop:10}}>
                        <label className="lc-label-sm">Unit <span className="lc-req">*</span></label>
                        <select className="lc-select" value={lcDoorId} onChange={e => handleLcUnitChange(e.target.value)}>
                          <option value="">Select unit…</option>
                          {lcProperty.units.map((u, i) => (
                            <option key={u.doorId} value={u.doorId}>
                              {u.unitNumber ? `Unit ${u.unitNumber}` : `Unit ${String.fromCharCode(65+i)}`} — {u.status}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>

                  <div className="lc-fees-section" style={{ marginTop: 14 }}>
                    <div className="lc-fees-title">Additional Fees <span className="lc-optional">(Optional)</span></div>
                    <div className="lc-fees-box">
                      {lcAiFees.map((fee, i) => (
                        <div key={i} className="lc-fee-row">
                          <input className="lc-fee-name" placeholder="Fee name" value={fee.name}
                            onChange={e => setLcAiFees(prev => prev.map((f, j) => j === i ? { ...f, name: e.target.value } : f))} />
                          <input className="lc-fee-amount" placeholder="Amount" type="number" min="0" value={fee.amount}
                            onChange={e => setLcAiFees(prev => prev.map((f, j) => j === i ? { ...f, amount: e.target.value } : f))} />
                          {lcAiFees.length > 1 && (
                            <button className="lc-fee-remove" onClick={() => setLcAiFees(prev => prev.filter((_, j) => j !== i))}><FaTimes /></button>
                          )}
                        </div>
                      ))}
                      <button className="lc-add-fee-btn" onClick={() => setLcAiFees(prev => [...prev, { name: '', amount: '' }])}>
                        <FaPlus /> Add Fee
                      </button>
                      <p className="lc-fees-hint">Add any additional monthly fees like pet fees, parking, or storage.</p>
                    </div>
                  </div>

                  <button className="lc-save-btn" style={{marginTop:20}} onClick={() => {
                    if (!lcPropertyId || (lcIsMulti && !lcDoorId)) { setLcAiError('Please select a property and unit.'); return; }
                    if (!lcAiForm.tenantName.trim() || !lcAiForm.rent) { setLcAiError('Tenant name and rent are required.'); return; }
                    setLcAiError('');
                    setLcStep('ai-confirm');
                  }}>
                    Continue →
                  </button>
                </div>
              </>
            )}

            {/* ---- AI: CONFIRM STEP ---- */}
            {lcStep === 'ai-confirm' && (
              <>
                <div className="lc-header">
                  <button className="lc-back-btn" onClick={() => setLcStep('ai-review')}>← Edit</button>
                  <h2 className="lc-title">Confirm Import</h2>
                  <button className="lc-close-btn" onClick={closeLC}><FaTimes /></button>
                </div>
                <div className="lc-body lc-form-body">
                  <p className="lc-ai-step-intro">Double-check before importing.</p>
                  <div className="lc-ai-confirm-card">
                    {[
                      ['Tenant', lcAiForm.tenantName || '—'],
                      ['Phone', lcAiForm.phone || '—'],
                      ['Email', lcAiForm.email || '—'],
                      ['Monthly Rent', lcAiForm.rent ? `$${Number(lcAiForm.rent).toLocaleString()}` : '—'],
                      ['Deposit', lcAiForm.deposit ? `$${Number(lcAiForm.deposit).toLocaleString()}` : '—'],
                      ['Lease Start', lcAiForm.leaseStart || '—'],
                      ['Lease End', lcAiForm.leaseEnd || '—'],
                      ['Insurance Start', lcAiForm.insuranceStart || '—'],
                      ['Insurance End', lcAiForm.insuranceEnd || '—'],
                      ['Rent Due', lcAiForm.rentDue ? `Day ${lcAiForm.rentDue}` : '—'],
                      ['Lease Type', lcAiForm.leaseType],
                      ['Property', lcProperty?.name || '—'],
                      ['Unit', (() => {
                        if (!lcIsMulti) return 'Single Unit';
                        const u = lcProperty?.units.find(u => u.doorId === lcDoorId);
                        const i = lcProperty?.units.indexOf(u) ?? 0;
                        return u?.unitNumber ? `Unit ${u.unitNumber}` : `Unit ${String.fromCharCode(65 + i)}`;
                      })()],
                    ].map(([lbl, val]) => (
                      <div key={lbl} className="lc-ai-confirm-row">
                        <span className="lc-ai-confirm-lbl">{lbl}</span>
                        <span className="lc-ai-confirm-val">{val}</span>
                      </div>
                    ))}
                  </div>
                  <label className="lc-ai-save-doc-label">
                    <input type="checkbox" checked={lcAiSaveDoc} onChange={e => setLcAiSaveDoc(e.target.checked)} />
                    <span>Save original lease document to server</span>
                  </label>
                  {lcAiError && <div className="lc-error" style={{marginTop:8}}>{lcAiError}</div>}
                  <button className="lc-save-btn" onClick={handleAiImport} disabled={lcSubmitting}
                    style={{marginTop:16,display:'inline-flex',alignItems:'center',gap:8,justifyContent:'center'}}>
                    {lcSubmitting ? <><FaSpinner className="lc-spin" /> Importing…</> : 'Import Lease'}
                  </button>
                </div>
              </>
            )}

          </div>
        </div>
      )}

      {userEmail && (
        <Tutorial
          steps={[
            { title: 'Your Properties Hub', description: 'This is where you manage your entire rental portfolio — units, occupancy, leases, and more.', anchorId: null, cardAlignment: 'bottom' },
            { title: 'Occupancy Overview', description: 'These stat cards show your total properties, units, occupied and vacant counts at a glance.', anchorId: 'props-stat-cards' },
            { title: 'Your Portfolio', description: 'All your properties are listed here as cards. Each one shows its name, address, type, and current occupancy status.', anchorId: 'props-list' },
            { title: 'View Details', description: "Tap 'View Details' to expand a property card — see individual units with their occupancy, jump into Property Details, or pull a full Financial Report.", anchorId: 'props-first-card' },
            { title: 'Filter your Properties', description: 'The Filters panel lets you narrow down by property name, address, type (Single or Multi Family), or occupancy status. Everything updates instantly.', anchorId: 'props-filter-drawer' },
            { title: 'Add Property & Leasing', description: 'Add a new property, open the Leasing Center to manage tenant leases, or jump straight into a financial projection — all from these buttons.', anchorId: 'props-action-btns' },
          ]}
          storageKey={`hasSeenPropertiesTutorial_${userEmail}`}
          onStep={handleTutorialStep}
        />
      )}
      {confirmDialog && (
        <ConfirmModal
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmLabel={confirmDialog.confirmLabel}
          variant={confirmDialog.variant || 'danger'}
          onConfirm={() => { confirmDialog.onConfirm(); setConfirmDialog(null); }}
          onCancel={() => setConfirmDialog(null)}
        />
      )}
    </div>
  );
}

export default Properties;
